package com.bikompanion.data.analytics.web

import com.bikompanion.domain.analytics.AnalyticsEvent

private const val MicrosPerMilli = 1_000L
private const val DefaultPageTitle = "Bikompanion"

/**
 * Queues web events and hands them to [send] as GA4 Measurement Protocol batches.
 *
 * A screen change produces several events at once, and one request per event would be wasteful
 * and easy to lose on navigation. The queue flushes on a short delay through [scheduleFlush],
 * as soon as it holds a full batch, and whenever the host calls [flush] because the page is
 * going away.
 *
 * The protocol has no page context of its own, so each event carries one. The page title is the
 * last reported screen, since the app is a single page whose path only names a station, and the
 * location is [pageLocation] without that path for the same reason.
 */
internal class Ga4EventQueue(
    private val identity: WebAnalyticsIdentity,
    private val defaultParameters: Map<String, Any>,
    private val userProperties: Map<String, String>,
    private val pageLocation: String,
    private val nowMillis: () -> Long,
    private val scheduleFlush: (flush: () -> Unit) -> Unit,
    private val send: (payload: String) -> Unit,
) {
    private val queue = mutableListOf<WebAnalyticsRecord>()
    private var isFlushScheduled = false
    private var pageTitle = DefaultPageTitle

    fun track(event: AnalyticsEvent) {
        if (event.name == "screen_view") (event.parameters["screen_name"] as? String)?.let { pageTitle = it }
        val capturedAt = nowMillis()
        queue += WebAnalyticsRecord(
            event = event,
            context = identity.resolve(capturedAt),
            timestampMicros = capturedAt * MicrosPerMilli,
            pageParameters = mapOf("page_title" to pageTitle, "page_location" to pageLocation),
        )
        if (queue.size >= MaxEventsPerBatch) {
            flush()
        } else if (!isFlushScheduled) {
            isFlushScheduled = true
            scheduleFlush(::flush)
        }
    }

    fun flush() {
        isFlushScheduled = false
        if (queue.isEmpty()) return
        val payloads = webAnalyticsPayloads(queue.toList(), defaultParameters, userProperties)
        queue.clear()
        payloads.forEach(send)
    }
}
